import React, { useRef, useState, useEffect } from 'react';
import CanvasQuiltRenderer from './CanvasQuiltRenderer';

/**
 * Component to render the quilt using Canvas instead of HTML/CSS
 */
const QuiltDisplay = ({ 
    selectedLayout, 
    showBinding, 
    showBorder, 
    bindingColor, 
    borderColor,
    embossedItems,
    textItems,
    canvasRef, // kept for backward compatibility
    firestoreDimensions // Accept the dimensions from parent
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [renderKey, setRenderKey] = useState(Date.now());
    
    // Container ref for measuring available space
    const containerRef = useRef(null);
    // Internal canvas ref 
    const internalCanvasRef = useRef(null);
    
    // Track container dimensions for responsive canvas sizing
    const [containerDimensions, setContainerDimensions] = useState({
        width: 800,
        height: 800
    });
    
    // Observe container size changes
    useEffect(() => {
        if (!containerRef.current) return;
        
        // Create ResizeObserver to track container size changes
        const resizeObserver = new ResizeObserver(entries => {
            const entry = entries[0];
            if (entry) {
                const { width, height } = entry.contentRect;
                // Keep square aspect ratio for the canvas
                const size = Math.min(width, height);
                setContainerDimensions({
                    width: size,
                    height: size
                });
            }
        });
        
        resizeObserver.observe(containerRef.current);
        
        return () => {
            resizeObserver.disconnect();
        };
    }, []);
    
    // Pass the canvas ref for backward compatibility
    useEffect(() => {
        if (canvasRef && internalCanvasRef.current) {
            if (typeof canvasRef === 'function') {
                canvasRef(internalCanvasRef.current);
            } else if (canvasRef.current) {
                // Copy properties and methods
                Object.assign(canvasRef.current, internalCanvasRef.current);
            }
        }
    }, [canvasRef, internalCanvasRef.current]);
    
    // Update renderKey whenever embossedItems or textItems change to force fresh rendering
    useEffect(() => {
        // Generate a new renderKey when embossed items or text items change
        // This will force a complete re-render of the canvas
        setRenderKey(Date.now());
    }, [embossedItems, textItems]);
    
    // Handle render complete
    const handleRenderComplete = () => {
        setIsLoading(false);
    };

    // Extract grid size from selected layout
    const getGridSizeFromLayout = () => {
        if (!selectedLayout) return null;
        
        return selectedLayout.gridSize || 
               selectedLayout.original_grid_size || 
               (selectedLayout.tileData && 
                Array.isArray(selectedLayout.tileData) && 
                selectedLayout.tileData.length > 0 && 
                `${Math.max(...selectedLayout.tileData.map(t => t.col || 0)) + 1}x${Math.max(...selectedLayout.tileData.map(t => t.row || 0)) + 1}`);
    };
    
    const layoutGridSize = getGridSizeFromLayout();

    // If there's a rendering error
    if (error) {
        return (
            <div className="relative w-full h-full flex items-center justify-center bg-red-50">
                <div className="text-center p-4">
                    <p className="text-red-600 mb-2">Error: {error}</p>
                    <p className="text-sm text-gray-600">Unable to render the quilt layout</p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative w-full h-full" ref={containerRef} style={{
            // IMPROVED: Better text rendering without affecting tile sharpness
            imageRendering: 'auto',
            WebkitImageRendering: 'auto',
            msImageRendering: 'auto',
            transform: 'translateZ(0)', 
            WebkitTransform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            // CRITICAL: Optimize for text clarity
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            textRendering: 'optimizeLegibility',
            fontSmooth: 'always',
            WebkitTapHighlightColor: 'transparent',
            // Force hardware acceleration for smoother rendering
            WebkitPerspective: '1000px',
            WebkitTransformStyle: 'preserve-3d',
            isolation: 'isolate',
            willChange: 'transform',
            contain: 'layout style paint'
        }}>
            <CanvasQuiltRenderer
                key={renderKey}
                selectedLayout={selectedLayout}
                showBinding={showBinding}
                showBorder={showBorder}
                bindingColor={bindingColor}
                borderColor={borderColor}
                embossedItems={embossedItems || []}
                textItems={textItems || []}
                width={containerDimensions.width}
                height={containerDimensions.height}
                canvasRef={internalCanvasRef}
                onRenderComplete={handleRenderComplete}
                gridSize={layoutGridSize}
                firestoreDimensions={firestoreDimensions}
                style={{
                    // IMPROVED: Canvas-specific styling for text clarity
                    imageRendering: 'auto',
                    WebkitImageRendering: 'auto',
                    display: 'block',
                    maxWidth: '100%',
                    height: 'auto',
                    WebkitTransform: 'translate3d(0,0,0)',
                    transform: 'translate3d(0,0,0)',
                    // CRITICAL: Ensure text renders smoothly
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    textRendering: 'optimizeLegibility',
                    fontSmooth: 'always',
                    // Allow smooth rendering for text while keeping other content crisp
                    imageRendering: 'high-quality',
                    WebkitFilter: 'none',
                    filter: 'none'
                }}
            />
            
            {isLoading && (
                <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
                    <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        <p className="mt-2 text-sm text-gray-600">Loading layout...</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuiltDisplay;
